import type { SupabaseClient } from '@supabase/supabase-js'
import type { IInvitationRepository, Invitation, InvitationStatus, Role, CreateInvitationData } from '@knownow/domain'
import { NotFoundError } from '@knownow/domain'
import type { Database } from '@knownow/db'

type InvitationRow = Database['org']['Tables']['invitations']['Row']

export class SupabaseInvitationRepository implements IInvitationRepository {
  constructor(private readonly client: SupabaseClient<Database>) {}

  async getById(id: string): Promise<Invitation> {
    const { data, error } = await this.client
      .schema('org')
      .from('invitations')
      .select('*')
      .eq('id', id)
      .single()

    if (error !== null || data === null) {
      throw new NotFoundError('Invitation', id)
    }

    return this.toDomain(data)
  }

  async getByToken(token: string): Promise<Invitation> {
    const { data, error } = await this.client
      .schema('org')
      .from('invitations')
      .select('*')
      .eq('token', token)
      .single()

    if (error !== null || data === null) {
      throw new NotFoundError('Invitation', token)
    }

    return this.toDomain(data)
  }

  async getByOrgId(organizationId: string): Promise<readonly Invitation[]> {
    const { data, error } = await this.client
      .schema('org')
      .from('invitations')
      .select('*')
      .eq('organization_id', organizationId)

    if (error !== null) {
      throw error
    }

    return (data ?? []).map(this.toDomain)
  }

  async create(data: CreateInvitationData): Promise<Invitation> {
    const { data: row, error } = await this.client
      .schema('org')
      .from('invitations')
      .insert({
        organization_id: data.organizationId,
        email: data.email ?? null,
        role: data.role,
        invited_by: data.invitedBy,
      })
      .select()
      .single()

    if (error !== null || row === null) {
      throw error ?? new Error('Failed to create invitation')
    }

    return this.toDomain(row)
  }

  async updateStatus(id: string, status: InvitationStatus): Promise<void> {
    const { error } = await this.client
      .schema('org')
      .from('invitations')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', id)

    if (error !== null) {
      throw error
    }
  }

  private toDomain(row: InvitationRow): Invitation {
    return {
      id: row.id,
      organizationId: row.organization_id,
      email: row.email,
      role: row.role as Role,
      invitedBy: row.invited_by,
      token: row.token,
      status: row.status as InvitationStatus,
      expiresAt: row.expires_at ? new Date(row.expires_at) : null,
      createdAt: new Date(row.created_at),
      updatedAt: new Date(row.updated_at),
    }
  }
}
